/* eslint-disable react/no-children-prop */
import { PageOptions } from '@graphcommerce/framer-next-pages'
import { hygraphPageContent, HygraphPagesQuery } from '@graphcommerce/graphcms-ui'
import { flushMeasurePerf, useQuery } from '@graphcommerce/graphql'
import { Image } from '@graphcommerce/image'
import { getCategoryStaticPaths } from '@graphcommerce/magento-category'
import { useCustomerQuery } from '@graphcommerce/magento-customer'
import {
  extractUrlQuery,
  FilterTypes,
  getFilterTypes,
  ProductFiltersDocument,
  ProductFiltersQuery,
  ProductListDocument,
  ProductListParams,
  ProductListParamsProvider,
  ProductListQuery,
} from '@graphcommerce/magento-product'
import { redirectOrNotFound, StoreConfigDocument } from '@graphcommerce/magento-store'
import { GetStaticProps, MetaRobots } from '@graphcommerce/next-ui'

import { i18n } from '@lingui/core'
import { Box, NoSsr, Paper, Rating, Tooltip } from '@mui/material'
import { GetStaticPaths } from 'next'
import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  LayoutNavigation,
  LayoutNavigationProps,
  ProductListItems,
  RowProduct,
  RowRenderer,
} from '../components'
import { CategoryArtworkSection } from '../components/Category/CategoryArtworkSection'
import { CategoryBreadcrumb } from '../components/Category/CategoryBreadcrumb/CategoryBreadcrumb'
import { CategoryChildSlider } from '../components/Category/CategoryChildSlider'
import { CategoryExtraDescription } from '../components/Category/CategoryExtraDescription'
import { CategoryFreeDesignSection } from '../components/Category/CategoryFreeDesignSection'
// import { CategoryImageGrid } from '../components/Category/CategoryImageGrid'
import { CategoryMeta } from '../components/Category/CategoryMeta/CategoryMeta'
import { CategoryProductImageGrid } from '../components/Category/CategoryProductImageGrid'
import {
  AmFiles,
  CategoryProductImageSlider,
} from '../components/Category/CategoryProductImageSlider'
import { createCategoryProductItem } from '../components/Category/CategoryProductItem'
import { CategoryQuestionsAndAnswers } from '../components/Category/CategoryQuestionsAndAnswers/CategoryQuestionsAndAnswers'
import { ResponsiveImageGridItem } from '../components/Image/ResponsiveImageGridItem'
import { Icon } from '../components/Layout/Icon'
import { LayoutDocument } from '../components/Layout/Layout.gql'
import { JsonLd } from '../components/NextUi/JsonLd/JsonLd'
import { ProductListFilters } from '../components/Product/ProductListFilters/ProductListFilters'
import { helpItem } from '../components/Product/ProductListItem/HelpListItem'
import { parseParams } from '../components/Product/ProductListItems/filteredProductList'
import { ProductListPagination } from '../components/Product/ProductListPagination/ProductListPagination'
import { ProductListPerPage } from '../components/Product/ProductListPerPage/ProductListPerPage'
import { ProductListSort } from '../components/Product/ProductListSort/ProductListSort'
import {
  AmastySortFieldsDocument,
  AmastySortFieldsQuery,
} from '../components/Product/ProductListSort/ProductListSort.gql'
import { CategoryReviews } from '../components/Reviews/CategoryReviews'
import { ProductQuestionsAndAnswers } from '../components/Reviews/ProductQuestionsAndAnswers'
import { ProductReviews } from '../components/Reviews/ProductReviews'
import { ReviewItemProps } from '../components/Reviews/ReviewItem'
import { reviewData } from '../components/Reviews/data'
import { FormattedRichTextBox } from '../components/RichText/FormattedRichTextBox'
import { RichTextBox } from '../components/RichText/RichTextBox'
import { AmFAQCategoryDocument } from '../graphql/AmFAQCategory.gql'
import { CategoryPageDocument, CategoryPageQuery } from '../graphql/CategoryPage.gql'
import { CustomerDocument } from '../graphql/Customer.gql'
import { useResetCheckout } from '../hooks/useResetCheckout'
import useWindowSize from '../hooks/useWindowSize'
import { graphqlSharedClient, graphqlSsrClient } from '../lib/graphql/graphqlSsrClient'

export type CategoryProps = CategoryPageQuery &
  HygraphPagesQuery &
  ProductListQuery &
  AmastySortFieldsQuery &
  ProductFiltersQuery & { filterTypes?: FilterTypes; params?: ProductListParams }
export type CategoryRoute = { url: string[] }
type GetPageStaticPaths = GetStaticPaths<CategoryRoute>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, CategoryProps, CategoryRoute>

function CategoryPage(props: CategoryProps) {
  const { categories, products, filters, params, filterTypes, pages, amOrderList } = props
  const { isMobile } = useWindowSize()
  useResetCheckout()

  const category = categories?.items?.[0]
  const isLanding = category?.display_mode === 'PAGE'
  const page = pages?.[0]
  const isCategory = params && category && products?.items && filterTypes
  const [hasScrolledDown, setHasScrollDown] = useState(false)
  const [showCategoryProductsImageGrid, setShowCategoryProductsImageGrid] = useState<boolean>(false)

  const productListRef = useRef(null)
  const productImageListRef = useRef(null)

  const dashboard = useCustomerQuery(CustomerDocument, {
    fetchPolicy: 'cache-and-network',
  })
  const customer = dashboard.data?.customer

  const handleScrollToProductImageList = () => {
    setShowCategoryProductsImageGrid(true)
    window.scrollTo({
      // eslint-disable-next-line no-unsafe-optional-chaining
      top: (productImageListRef?.current as any)?.offsetTop - 70,
      behavior: 'smooth',
    })
  }

  // scroll to the top of the page whenever a new page is loaded
  useEffect(() => {
    if (!category?.amfiles || !category?.amfiles?.length) {
      setShowCategoryProductsImageGrid(false)
    }
  }, [category?.amfiles])

  const categoryFAQs =
    useQuery(AmFAQCategoryDocument, {
      fetchPolicy: 'cache-and-network',
      variables: {
        pageSize: 100,
      },
    })?.data?.searchAmFaqCategories?.items ?? []

  const qaData = category
    ? categoryFAQs
        .filter((item) => item?.url_key?.replaceAll('_', '/') === category?.url_path)
        ?.at(0)?.questions
    : []

  const handleScrollToProductList = () => {
    // window.scrollTo({
    //   // eslint-disable-next-line no-unsafe-optional-chaining
    //   top: (productListRef?.current as any)?.offsetTop - 70,
    //   behavior: 'smooth',
    // })
  }

  const amFileUrl = '/amfile/file/download/file/482/'
  const fileId = category?.amfiles?.at(0)?.file_id
  const childUrl = category?.amfiles?.at(0)?.url
  const categoryProductsLinkItem = createCategoryProductItem({
    uid: 'link-item-123',
    name: 'Your art, In real life',
    image: `${childUrl}/${amFileUrl}/${fileId}` ?? '',
    hashtag: '#PRINTBYTEMPLI',
    onClick: () => handleScrollToProductImageList(),
    isLinkItem: true,
  })

  const modifiedCategoryChildren = useMemo(() => {
    if (category?.amfiles && category.amfiles.length > 0) {
      return [...(category?.children ?? []), categoryProductsLinkItem]
    }
    return category?.children
  }, [category?.amfiles, category?.children, categoryProductsLinkItem])

  const averageScoreRouned = Math.round((category?.yotpo_average_rating ?? 0) * 10) / 10
  const reviewsSectionRef = useRef(null)
  const handleScrollToReviews = () => {
    window.scrollTo({
      // eslint-disable-next-line no-unsafe-optional-chaining
      top: (reviewsSectionRef?.current as any)?.offsetTop - 70,
      behavior: 'smooth',
    })
  }
  const heading = useMemo(() => {
    // eslint-disable-next-line no-unsafe-optional-chaining

    if (category) {
      return (
        <NoSsr>
          <div className='my-3 flex w-full items-center justify-center px-6 md:mb-[60px] md:mt-0 md:w-full md:px-[5%] '>
            <div className='flex flex-col gap-4 text-black'>
              <h1 className='flex flex-col items-start justify-center text-[26px] font-medium md:text-[30px] md:font-semibold lg:flex-row lg:items-center lg:justify-start'>
                {category?.h1_content ?? category?.name ?? page.title}
                {category?.yotpo_review_count && category?.yotpo_review_count > 0 && (
                  <button
                    type='button'
                    onClick={handleScrollToReviews}
                    className='Type-Base-Medium lg:Type-Large-Medium ml-[-12px] flex items-center text-black lg:ml-6'
                  >
                    <Rating
                      name='total-ratings'
                      defaultValue={averageScoreRouned}
                      size='medium'
                      readOnly
                      precision={0.1}
                      className='px-2 align-bottom'
                    />
                    <span className='Type-XL-Regular inline'>
                      ({averageScoreRouned}) {category?.yotpo_review_count ?? 0} Reviews
                    </span>
                    {/* <span className='Type-XL-Regular ml-2 hidden italic text-medium-dark-gray md:inline'>
                      - See product pages for reviews
                    </span> */}
                  </button>
                )}
                {/* <span className='Type-XL-Regular ml-2 inline italic text-medium-dark-gray md:hidden'>
                  See product pages for reviews
                </span> */}
              </h1>
              {category?.description && category?.description?.length > 0 && (
                <h2 className='Type-XL-Regular md:Type-XXXL-Regular block'>
                  <FormattedRichTextBox
                    htmlContent={category?.description}
                    className='Type-XL-Regular md:Type-XXL-Regular'
                  />
                </h2>
              )}
            </div>
          </div>
        </NoSsr>
      )
    }
    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, modifiedCategoryChildren, page?.title, showCategoryProductsImageGrid])

  const modifiedProductListItems =
    products && products?.items && products?.items?.length > 4
      ? // eslint-disable-next-line no-unsafe-optional-chaining
        [...products?.items?.slice(0, 4), helpItem, ...products?.items?.slice(4)]
      : // eslint-disable-next-line no-unsafe-optional-chaining
        [...(products?.items ?? []), helpItem]

  const reviews = category?.reviews?.items

  return (
    <div className='m-auto mt-0 w-full lg:max-w-maxContentWidth 2xl:px-0'>
      {category?.yotpo_review_count && category?.yotpo_average_rating && (
        <JsonLd
          identifier='category-page'
          item={{
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: category?.name,
            description: category?.description,
            url: `https://www.templi.com/${category?.url_path}`,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: category?.yotpo_average_rating,
              reviewCount: category?.yotpo_review_count,
              bestRating: 5,
              worstRating: 1,
            },
            ...(reviews &&
              reviews.length > 0 && {
                review: reviews
                  .filter((review) => review !== null && review !== undefined && review?.score)
                  .slice(0, 10)
                  .map((review) => ({
                    '@type': 'Review',
                    author: {
                      '@type': 'Person',
                      name: (review as any)?.user_display_name || 'Anonymous',
                    },
                    datePublished: (review as any)?.created_at || new Date().toISOString(),
                    reviewBody: review?.content || '',
                    name: review?.title || '',
                    reviewRating: {
                      '@type': 'Rating',
                      ratingValue: review?.score || 0,
                      bestRating: 5,
                      worstRating: 1,
                    },
                  })),
              }),
          }}
        />
      )}
      <CategoryMeta
        params={params}
        title={page?.metaTitle ?? page?.title}
        metaDescription={page?.metaDescription}
        metaRobots={[] as MetaRobots[]}
        canonical={page?.url ? `/${page.url}` : undefined}
        {...category}
      />
      {!isMobile && category && (
        <div className='md:mx-auto md:pl-5 2xl:pl-0'>
          <CategoryBreadcrumb category={category} />{' '}
        </div>
      )}

      {isCategory && !isLanding && (
        <ProductListParamsProvider value={params}>
          <div className='w-full'>
            {isMobile && heading}
            <div className='mb-5 w-full bg-pure-white md:flex md:gap-[20px]'>
              <div ref={productListRef} className='w-full'>
                {!isMobile && heading}
                <div className='mx-6 mt-[30px] rounded-[24px] py-5 md:mx-[5%] md:px-0 md:py-0'>
                  <ProductListItems
                    title={category.name ?? ''}
                    items={products?.items}
                    loadingEager={1}
                    columns={3}
                  />
                  <div className='mt-[40px]'>
                    <ProductListPagination page_info={products?.page_info} params={params} />
                  </div>
                </div>
                {category?.extra_description && category?.extra_description.length > 0 && (
                  <NoSsr>
                    <CategoryExtraDescription category={category} />
                  </NoSsr>
                )}

                {category?.video && (
                  <div className='my-5 mx-0 md:mx-[10%] flex w-auto flex-col items-center justify-center rounded-[16px] bg-[#FFFDF3CC] p-6 md:p-10'>
                    <div className='aspect-video w-auto rounded-[12px] flex items-center justify-center'>
                      {category?.video?.includes('s3.amazonaws.com') ||
                      category?.video?.endsWith('.mp4') ? (
                        <video
                          key={category.video}
                          width='100%'
                          height='100%'
                          autoPlay
                          loop
                          muted
                          playsInline
                          controls={false}
                          className='rounded-[12px] object-cover'
                        >
                          <source src={category.video} type='video/mp4' />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <iframe
                          width='100%'
                          height='100%'
                          src={`${category?.video}${
                            category?.video?.includes('?') ? '&' : '?'
                          }autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&showinfo=0&loop=1&playlist=${
                            category?.video?.split('/').pop()?.split('?')[0]
                          }&iv_load_policy=3&disablekb=1&playsinline=1`}
                          title='YouTube video player'
                          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                          allowFullScreen
                          frameBorder='0'
                          className='rounded-[12px]'
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* <NoSsr>
                  <CategoryImageGrid />
                </NoSsr> */}
                {category?.reviews?.items && category?.reviews?.items?.length > 0 && (
                  <NoSsr>
                    <div
                      className='mx-0 mt-[40px] rounded-[24px] bg-none md:mx-[10%]'
                      ref={reviewsSectionRef}
                    >
                      <CategoryReviews
                        customer={customer}
                        reviews={category?.reviews?.items}
                        averageScoreRouned={averageScoreRouned}
                        reviewsCount={category?.yotpo_review_count ?? 0}
                      />
                    </div>
                  </NoSsr>
                )}
                {qaData && qaData?.length > 0 && (
                  <NoSsr>
                    <div className='mx-0 mt-[40px] rounded-[24px] bg-[#F8FBFF] p-5 md:mx-[10%] md:px-20 md:py-10'>
                      <CategoryQuestionsAndAnswers qaData={qaData ?? []} displayInTab={false} />
                    </div>
                  </NoSsr>
                )}
              </div>
            </div>
          </div>
        </ProductListParamsProvider>
      )}

      {page && (
        <RowRenderer
          content={page.content}
          renderer={{
            RowProduct: (rowProps) => (
              <RowProduct
                {...rowProps}
                {...products?.items?.[0]}
                items={products?.items?.slice(0, 8)}
              />
            ),
          }}
        />
      )}
    </div>
  )
}
const pageOptions: PageOptions<LayoutNavigationProps> = {
  Layout: LayoutNavigation,
}
CategoryPage.pageOptions = pageOptions

export default CategoryPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  // Disable getStaticPaths while in development mode
  if (process.env.NODE_ENV === 'development' || process.env.SKIP_ISG)
    return { paths: [], fallback: 'blocking' }

  const path = (loc: string) => getCategoryStaticPaths(graphqlSsrClient(loc), loc)
  const paths = (await Promise.all(locales.map(path))).flat(1)
  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  const [url, query] = extractUrlQuery(params)
  if (!url || !query) return { notFound: true }

  const client = graphqlSharedClient(locale)
  const conf = client.query({ query: StoreConfigDocument })
  const filterTypes = getFilterTypes(client)

  const staticClient = graphqlSsrClient(locale)

  const categoryPage = staticClient.query({
    query: CategoryPageDocument,
    variables: { url },
  })
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  const productListParams = parseParams(url, query, await filterTypes)
  const filteredCategoryUid = productListParams && productListParams.filters.category_uid?.in?.[0]

  const category = categoryPage.then((res) => res.data.categories?.items?.[0])
  let categoryUid = filteredCategoryUid
  if (!categoryUid) {
    categoryUid = (await category)?.uid ?? ''
    if (productListParams) productListParams.filters.category_uid = { in: [categoryUid] }
  }

  const defaultSortBy = (await conf).data.storeConfig?.catalog_default_sort_by

  if (
    defaultSortBy &&
    productListParams &&
    typeof productListParams.sort === 'object' &&
    Object.keys(productListParams.sort) &&
    !Object.keys(productListParams.sort).length
  ) {
    productListParams.sort = {
      [defaultSortBy]: 'DESC',
    }
  }

  const pages = hygraphPageContent(staticClient, url, category)
  const hasPage = filteredCategoryUid ? false : (await pages).data.pages.length > 0
  const hasCategory = Boolean(productListParams && categoryUid)

  if (!productListParams || !(hasPage || hasCategory))
    return redirectOrNotFound(staticClient, conf, params, locale)

  if (!hasCategory) {
    return {
      props: {
        ...(await categoryPage).data,
        ...(await pages).data,
        ...(await layout).data,
        apolloState: await conf.then(() => client.cache.extract()),
      },
      revalidate: process.env.SKIP_ISG ? 1 : 60 * 5,
    }
  }

  const filters = staticClient.query({
    query: ProductFiltersDocument,
    variables: { filters: { category_uid: { eq: categoryUid } } },
  })
  const products = staticClient.query({
    query: ProductListDocument,
    variables: {
      pageSize: (await conf).data.storeConfig?.grid_per_page ?? 24,
      ...productListParams,
      filters: { ...productListParams.filters, category_uid: { eq: categoryUid } },
    },
  })

  if ((await products).errors) return { notFound: true }

  const { category_name, category_url_path } =
    (await categoryPage).data.categories?.items?.[0]?.breadcrumbs?.[0] ?? {}

  const up =
    category_url_path && category_name
      ? { href: `/${category_url_path}`, title: category_name }
      : { href: `/`, title: 'Home' }

  const amSortFields = staticClient.query({
    query: AmastySortFieldsDocument,
    variables: {},
  })

  const result = {
    props: {
      ...(await categoryPage).data,
      ...(await products).data,
      ...(await pages).data,
      ...(await filters).data,
      ...(await layout).data,
      ...(await amSortFields).data,
      filterTypes: await filterTypes,
      params: productListParams,
      apolloState: await conf.then(() => client.cache.extract()),
      up,
    },
    revalidate: process.env.SKIP_ISG ? 1 : 60 * 5,
  }
  flushMeasurePerf()
  return result
}
